import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UserRole } from "@shared/models/auth";
import type { ApprovalData } from "@/lib/role-helpers";

/**
 * SPEC-AdminImpersonation §"Architectural cleanup" (2026-05-29) —
 * Collapse `useAuth()` and `useRole()` to ONE source.
 *
 * Before: `useAuth()` queried `/api/auth/user`, and `App.tsx`
 * independently queried `/api/auth/check-approved` to seed `RoleContext`.
 * Two queries against two endpoints meant the banner and the route guards
 * could observe different "what role am I?" answers — exactly the
 * desync class of bug that bounced impersonated Sarah's `/qa/agent` to
 * `/qa/list`.
 *
 * After: both this hook AND `AuthenticatedApp`'s approval probe consume
 * the SAME query key (`AUTH_QUERY_KEY = ["/api/auth/check-approved"]`).
 * TanStack dedupes by key, so the two call sites observe one cache
 * entry. `staleTime: 0` — auth state is the one thing that must never
 * be served stale after an invalidation. The other ~80 queries in the
 * QAM keep the global `staleTime: Infinity` default per `queryClient.ts`.
 *
 * `useAuthData()` is the primitive both consumers call.
 */

// SPEC-Auth-Users-Roles-Audit + SPEC-AdminImpersonation — `/api/auth/check-approved`
// payload. `role` is the EFFECTIVE role; when `isImpersonating` is true the
// `actualRole` / `actualUserId` / `writesEnabled` fields describe the
// impersonator's real identity.
export interface AuthPayload extends ApprovalData {
  isImpersonating?: boolean;
  actualRole?: UserRole | null;
  actualUserId?: string | null;
  writesEnabled?: boolean;
}

// `AuthUser` shape consumed by qa-list / qa-detail-v3 / prescreen-detail-v3 /
// activetask-detail-v3 / annotation-review / early-risk-notifier /
// AppContent / impersonation-banner. Synthesized from the approval payload.
// `id` / `email` / `name` are kept `string | null` so consumers that do
// `user?.email ?? "Unknown"` keep their existing nullish-fallback behavior
// when the user is logged in but not approved (no row → no name).
export interface AuthUser {
  id: string | null;
  email: string | null;
  name: string | null;
  role: UserRole | null;
  isAdmin: boolean;
  dev: boolean;
  isImpersonating?: boolean;
  actualRole?: UserRole | null;
  actualUserId?: string | null;
  writesEnabled?: boolean;
}

// Single canonical query key. Importing this from `App.tsx` and `use-auth.ts`
// is the mechanism that "collapses to one source": TanStack dedupes by key.
export const AUTH_QUERY_KEY = ["/api/auth/check-approved"] as const;

async function fetchAuth(): Promise<AuthPayload | null> {
  const response = await fetch("/api/auth/check-approved", {
    credentials: "include",
  });
  if (response.status === 401) return null;
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return response.json();
}

async function logout(): Promise<void> {
  window.location.href = "/api/logout";
}

/**
 * Primitive — both `useAuth()` and the `AuthenticatedApp`'s approval probe
 * call this. One queryKey, one cache entry, one source of truth.
 *
 * `staleTime: 0` is the bug-killer: after `startImpersonation` /
 * `stopImpersonation` / `toggleWrites` invalidate the query, the next
 * render REFETCHES instead of serving the stale "I am still senior"
 * answer that was the root cause of the original race.
 */
export function useAuthData() {
  return useQuery<AuthPayload | null>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: fetchAuth,
    retry: false,
    staleTime: 0,
  });
}

function payloadToUser(payload: AuthPayload | null | undefined): AuthUser | null {
  if (!payload) return null;
  // Approved OR present-but-unapproved: both render with the AuthenticatedApp
  // shell (NotApprovedScreen for the latter). Only fully-unauthenticated
  // (401 → null payload) collapses to user = null → LandingPage.
  return {
    id: payload.userId ?? null,
    email: payload.email ?? null,
    name: payload.name ?? null,
    role: (payload.role ?? null) as UserRole | null,
    isAdmin: payload.isAdmin === true,
    dev: payload.dev === true,
    isImpersonating: payload.isImpersonating === true,
    actualRole: (payload.actualRole ?? null) as UserRole | null,
    actualUserId: payload.actualUserId ?? null,
    writesEnabled: payload.writesEnabled,
  };
}

export function useAuth() {
  const queryClient = useQueryClient();
  const { data: payload, isLoading } = useAuthData();

  const user = payloadToUser(payload);

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
    },
  });

  // SPEC-AdminImpersonation §"Client wiring #1" — surface the impersonation
  // fields alongside `user`. Existing code that reads `user.role` continues
  // to behave correctly because `role` IS `effectiveRole`.
  const isImpersonating = user?.isImpersonating === true;

  return {
    user,
    // Raw payload exposed so AuthenticatedApp can read `approved` without a
    // second query.
    approval: payload,
    isLoading,
    isAuthenticated: !!user,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
    // Convenience aliases — never throw on null user.
    effectiveRole: user?.role ?? null,
    actualRole: (user?.actualRole ?? user?.role) ?? null,
    actualUserId: (user?.actualUserId ?? user?.id) ?? null,
    isImpersonating,
    // Non-impersonating users always have writes enabled. When impersonating
    // the server-supplied flag is authoritative.
    writesEnabled: isImpersonating ? user?.writesEnabled === true : true,
  };
}
